// src/pages/relatorios/Relatorios.jsx
import React, { useState } from 'react';
import { 
  FaChartBar, FaTable, FaShoppingCart, FaArrowLeft, FaIdCard, FaUsers, FaHardHat, FaCheckCircle
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import RelatorioPedidos from './RelatorioPedidos';
import RelatorioPrestadores from './RelatorioPrestadores';
import RelatorioCredenciais from './RelatorioCredenciais';
import RelatorioMontadores from './RelatorioMontadores';
import RelatorioPedidosAprovados from './RelatorioPedidosAprovados';
import '../../styles/relatorios-hub.css';

const Relatorios = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { hasRole } = useAuth();

  // Definição dos relatórios por perfil
  const getAvailableReports = () => {
    const reports = [];

    // Relatório de Pedidos - disponível para admin e expositor
    if (hasRole('admin') || hasRole('expositor')) {
      reports.push({
        id: 'pedidos',
        name: 'Relatório de Pedidos',
        description: 'Análise completa de pedidos com detalhes de produtos, valores e descontos.',
        icon: <FaShoppingCart />,
        color: '#6f42c1',
        className: 'report-pedidos',
        badges: ['Pedidos', 'Valores', 'Filtros'],
        badgeColors: ['primary', 'success', 'info']
      });
    }

    // Relatório de Prestadores - disponível para admin e expositor
    if (hasRole('admin') || hasRole('expositor')) {
      reports.push({
        id: 'prestadores',
        name: 'Credenciais de Prestadores',
        description: 'Relatório completo de credenciais de prestadores de serviço cadastrados.',
        icon: <FaIdCard />,
        color: '#17a2b8',
        className: 'report-prestadores',
        badges: ['Credenciais', 'Prestadores', 'Empresas'],
        badgeColors: ['info', 'primary', 'success']
      });
    }

    // Relatório de Credenciais de Expositores - disponível para admin e expositor
    if (hasRole('admin') || hasRole('expositor')) {
      reports.push({
        id: 'credenciais',
        name: 'Credenciais de Expositores',
        description: 'Relatório detalhado de credenciais dos colaboradores dos expositores.',
        icon: <FaUsers />,
        color: '#28a745',
        className: 'report-credenciais',
        badges: ['Credenciais', 'Colaboradores', 'Expositores'],
        badgeColors: ['success', 'primary', 'info']
      });
    }

    // Relatório de Credenciais de Montadores - disponível para admin e expositor
    if (hasRole('admin') || hasRole('expositor')) {
      reports.push({
        id: 'montadores',
        name: 'Credenciais de Montadores',
        description: 'Relatório completo de credenciais de montadores e empresas montadoras.',
        icon: <FaHardHat />,
        color: '#fd7e14',
        className: 'report-montadores',
        badges: ['Credenciais', 'Montadores', 'Empresas'],
        badgeColors: ['warning', 'primary', 'success']
      });
    }

    // Relatório de Pedidos Aprovados - disponível APENAS para admin
    if (hasRole('admin')) {
      reports.push({
        id: 'pedidos-aprovados',
        name: 'Pedidos Aprovados',
        description: 'Análise detalhada de pedidos aprovados com métricas financeiras e de performance.',
        icon: <FaCheckCircle />,
        color: '#20c997',
        className: 'report-pedidos-aprovados',
        badges: ['Aprovados', 'Análise', 'Financeiro'],
        badgeColors: ['success', 'info', 'warning']
      });
    }

    return reports;
  };

  const availableReports = getAvailableReports();

  // Renderizar Dashboard Principal
  const renderDashboard = () => (
    <div className="relatorios-hub">
      {/* Header Section - Apple Style */}
      <div className="reports-header" style={{
        padding: '40px 24px 20px',
        background: 'linear-gradient(135deg, #fbfbfd 0%, #f5f5f7 100%)',
        borderBottom: '1px solid #e5e5e7'
      }}>
        <div className="reports-title-section" style={{
          textAlign: 'center',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h1 className="reports-main-title" style={{
            fontSize: '48px',
            fontWeight: '700',
            color: '#1d1d1f',
            margin: '0 0 16px 0',
            letterSpacing: '-0.02em',
            lineHeight: '1.1'
          }}>
            <FaChartBar style={{ 
              fontSize: '40px', 
              marginRight: '16px',
              color: '#161617ff',
              verticalAlign: 'middle'
            }} />
            Relatórios
          </h1>
          <p className="reports-subtitle" style={{
            fontSize: '21px',
            lineHeight: '1.4',
            color: '#86868b',
            fontWeight: '400',
            margin: '0'
          }}>
            Análises detalhadas e insights do seu negócio
          </p>
        </div>
      </div>

      {/* Navigation Tabs - Apple Style */}
      <div className="reports-navigation" style={{ 
        padding: '20px 0', 
        borderBottom: '1px solid #e5e5e7',
        background: '#fbfbfd'
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          justifyContent: 'center',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 24px'
        }}>
          <button
            className={`apple-nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: activeTab === 'dashboard' ? '600' : '400',
              background: activeTab === 'dashboard' ? '#161617ff' : 'transparent',
              color: activeTab === 'dashboard' ? '#fff' : '#1d1d1f',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
              boxShadow: activeTab === 'dashboard' ? '0 2px 8px rgba(0, 122, 255, 0.25)' : 'none',
              minHeight: '52px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'dashboard') {
                e.target.style.background = '#f5f5f7';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'dashboard') {
                e.target.style.background = 'transparent';
              }
            }}
          >
            <FaChartBar style={{ fontSize: '15px' }} />
            Dashboard
          </button>
          
          {availableReports.map((report) => {
            // Função para formatar o nome da aba
            const getTabName = (reportName) => {
              if (reportName === 'Relatório de Pedidos') return { line1: 'Pedidos', line2: 'com Itens' };
              if (reportName === 'Credenciais de Prestadores') return { line1: 'Credencial', line2: 'Prestadores' };
              if (reportName === 'Credenciais de Expositores') return { line1: 'Credencial', line2: 'Expositores' };
              if (reportName === 'Credenciais de Montadores') return { line1: 'Credencial', line2: 'Montadores' };
              if (reportName === 'Pedidos Aprovados') return { line1: 'Pedidos', line2: 'Aprovados' };
              return { single: reportName.replace('Relatório de ', '').replace('Credenciais de ', '') };
            };

            const isActive = activeTab === report.id;
            const tabName = getTabName(report.name);

            return (
              <button
                key={report.id}
                className={`apple-nav-tab ${isActive ? 'active' : ''}`}
                onClick={() => setActiveTab(report.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: isActive ? '600' : '400',
                  background: isActive ? '#161617ff' : 'transparent',
                  color: isActive ? '#fff' : '#1d1d1f',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  boxShadow: isActive ? '0 2px 8px rgba(0, 122, 255, 0.25)' : 'none',
                  flexDirection: tabName.line1 ? 'column' : 'row',
                  lineHeight: '1.2',
                  minHeight: tabName.line1 ? '52px' : 'auto'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.target.style.background = '#f5f5f7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                {tabName.line1 ? (
                  <>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      fontSize: '14px'
                    }}>
                      <span style={{ fontSize: '14px' }}>{report.icon}</span>
                      <span>{tabName.line1}</span>
                    </div>
                    <div style={{ 
                      fontSize: '16px',
                      fontWeight: isActive ? '600' : '500'
                    }}>
                      {tabName.line2}
                    </div>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '15px' }}>{report.icon}</span>
                    {tabName.single}
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Reports Grid - Apple Style */}
      <div className="reports-content" style={{ 
        padding: '40px 24px 60px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div className="section-header" style={{
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          <h2 className="section-title" style={{
            fontSize: '32px',
            fontWeight: '600',
            color: '#1d1d1f',
            margin: '0 0 12px 0',
            letterSpacing: '-0.01em'
          }}>Relatórios Disponíveis</h2>
          <p className="section-subtitle" style={{
            fontSize: '17px',
            color: '#86868b',
            margin: '0',
            lineHeight: '1.4'
          }}>
            Escolha um relatório para visualizar insights detalhados
          </p>
        </div>

        {availableReports.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '24px',
            marginBottom: '60px'
          }}>
            {availableReports.map((report, index) => (
              <div 
                key={report.id}
                onClick={() => setActiveTab(report.id)}
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  padding: '32px 24px',
                  cursor: 'pointer',
                  border: '1px solid #e5e5e7',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-4px)';
                  e.target.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.1)';
                  e.target.style.borderColor = '#161617ff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.05)';
                  e.target.style.borderColor = '#e5e5e7';
                }}
              >
                <div style={{
                  fontSize: '28px',
                  color: report.color,
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '56px',
                  height: '56px',
                  background: `${report.color}15`,
                  borderRadius: '12px'
                }}>
                  {report.icon}
                </div>

                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#1d1d1f',
                  margin: '0 0 8px 0',
                  lineHeight: '1.3'
                }}>{report.name}</h3>
                
                <p style={{
                  fontSize: '15px',
                  color: '#86868b',
                  margin: '0 0 20px 0',
                  lineHeight: '1.4'
                }}>{report.description}</p>

                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  marginBottom: '20px'
                }}>
                  {report.badges.map((badge, badgeIndex) => (
                    <span key={badgeIndex} style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      background: '#f5f5f7',
                      color: '#1d1d1f'
                    }}>
                      {badge}
                    </span>
                  ))}
                </div>

                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161617ff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  Abrir relatório →
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '60px 24px',
            color: '#86868b'
          }}>
            <FaTable style={{ fontSize: '48px', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '24px', fontWeight: '600', margin: '0 0 8px 0', color: '#1d1d1f' }}>
              Nenhum relatório disponível
            </h3>
            <p style={{ fontSize: '17px', margin: '0' }}>
              Não há relatórios configurados para seu perfil de acesso.
            </p>
          </div>
        )}

        {/* Features Showcase - Apple Style */}
        <div style={{
          background: '#f5f5f7',
          borderRadius: '20px',
          padding: '48px 32px',
          textAlign: 'center'
        }}>
          <div>
            <h3 style={{
              fontSize: '28px',
              fontWeight: '600',
              color: '#1d1d1f',
              margin: '0 0 12px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <FaChartBar style={{ fontSize: '24px', color: '#161617ff' }} />
              Sistema de Relatórios
            </h3>
            <p style={{
              fontSize: '17px',
              color: '#86868b',
              margin: '0 0 40px 0',
              lineHeight: '1.4',
              maxWidth: '600px',
              margin: '0 auto 40px auto'
            }}>
              Análises inteligentes e insights em tempo real para impulsionar seu negócio
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '32px',
              marginTop: '40px'
            }}>
              <div>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: '#fff',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}>
                  <FaTable style={{ fontSize: '24px', color: '#161617ff' }} />
                </div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: '#1d1d1f',
                  margin: '0 0 4px 0'
                }}>Dados Organizados</h4>
                <p style={{
                  fontSize: '15px',
                  color: '#86868b',
                  margin: '0',
                  lineHeight: '1.4'
                }}>Informações estruturadas e filtráveis</p>
              </div>
              
              <div>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: '#fff',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}>
                  <FaShoppingCart style={{ fontSize: '24px', color: '#161617ff' }} />
                </div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: '#1d1d1f',
                  margin: '0 0 4px 0'
                }}>Pedidos</h4>
                <p style={{
                  fontSize: '15px',
                  color: '#86868b',
                  margin: '0',
                  lineHeight: '1.4'
                }}>Acompanhe vendas e transações</p>
              </div>
              
              <div>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: '#fff',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}>
                  <FaIdCard style={{ fontSize: '24px', color: '#161617ff' }} />
                </div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: '#1d1d1f',
                  margin: '0 0 4px 0'
                }}>Prestadores</h4>
                <p style={{
                  fontSize: '15px',
                  color: '#86868b',
                  margin: '0',
                  lineHeight: '1.4'
                }}>Controle de prestadores de serviço</p>
              </div>
              
              <div>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: '#fff',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}>
                  <FaUsers style={{ fontSize: '24px', color: '#161617ff' }} />
                </div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: '#1d1d1f',
                  margin: '0 0 4px 0'
                }}>Expositores</h4>
                <p style={{
                  fontSize: '15px',
                  color: '#86868b',
                  margin: '0',
                  lineHeight: '1.4'
                }}>Gestão de colaboradores</p>
              </div>
              
              <div>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: '#fff',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}>
                  <FaHardHat style={{ fontSize: '24px', color: '#161617ff' }} />
                </div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: '#1d1d1f',
                  margin: '0 0 4px 0'
                }}>Montadores</h4>
                <p style={{
                  fontSize: '15px',
                  color: '#86868b',
                  margin: '0',
                  lineHeight: '1.4'
                }}>Controle de montadores e empresas</p>
              </div>
              
              <div>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: '#fff',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}>
                  <FaCheckCircle style={{ fontSize: '24px', color: '#161617ff' }} />
                </div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: '#1d1d1f',
                  margin: '0 0 4px 0'
                }}>Análise Financeira</h4>
                <p style={{
                  fontSize: '15px',
                  color: '#86868b',
                  margin: '0',
                  lineHeight: '1.4'
                }}>Pedidos aprovados e métricas</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Renderizar relatório específico com header
  const renderReport = (reportId, title, icon, component) => (
    <div className="report-view">
      <div className="report-header">
        <button 
          className="back-button"
          onClick={() => setActiveTab('dashboard')}
        >
          <FaArrowLeft />
          Voltar para Dashboard
        </button>
        
        <div className="report-title-section">
          <h1 className="report-view-title">
            {icon}
            {title}
          </h1>
          <p className="report-view-subtitle">Análise completa e detalhada</p>
        </div>
      </div>
      
      <div className="report-content">
        {component}
      </div>
    </div>
  );

  // Renderizar conteúdo baseado na aba ativa
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'pedidos':
        return renderReport('pedidos', 'Relatório de Pedidos', <FaShoppingCart />, <RelatorioPedidos />);
      case 'prestadores':
        return renderReport('prestadores', 'Credenciais de Prestadores', <FaIdCard />, <RelatorioPrestadores />);
      case 'credenciais':
        return renderReport('credenciais', 'Credenciais de Expositores', <FaUsers />, <RelatorioCredenciais />);
      case 'montadores':
        return renderReport('montadores', 'Credenciais de Montadores', <FaHardHat />, <RelatorioMontadores />);
      case 'pedidos-aprovados':
        return renderReport('pedidos-aprovados', 'Pedidos Aprovados', <FaCheckCircle />, <RelatorioPedidosAprovados />);
      default:
        return renderDashboard();
    }
  };
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {renderContent()}
      </div>
    </div>
  );
};

export default Relatorios;